// src/components/Purchase/PurchaseOrderView.jsx
/**
 * PurchaseOrderView.jsx (FEDIS - Détail BC)
 * =========================================
 * Rôle:
 * - Affiche le détail d'un Bon de commande (BC)
 * - Permet:
 *   - de valider un BC (draft -> approved)
 *   - de télécharger le PDF du BC
 *   - de convertir le BC en Bon d'achat (BA) via endpoint serveur
 *
 * Routing:
 * - Doit être rendu sous /purchases/orders/:id
 *
 * Dépendances API:
 * - GET  /api/purchase-orders/:id
 * - POST /api/purchase-orders/:id/validate
 * - GET  /api/purchase-orders/:id/pdf       (uuid OU external_id)
 * - POST /api/purchase-orders/:id/convert-to-receipt (crée BA + stock++)
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch, apiFetchBlob } from '../../api.js'

const money = (v) => Number(v ?? 0).toLocaleString('fr-FR')

function forceDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

function StatusBadge({ status }) {
  const s = (status || 'draft').toLowerCase()
  const map = {
    draft: 'bg-surface-hover text-content-primary border-border',
    approved: 'bg-slate-50 text-slate-800 border-slate-100',
    sent: 'bg-slate-50 text-slate-800 border-slate-100',
    partially_received: 'bg-amber-50 text-amber-800 border-amber-100',
    received: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    cancelled: 'bg-rose-50 text-rose-700 border-rose-100',
  }
  const label = {
    draft: 'Brouillon',
    approved: 'Validé',
    sent: 'Envoyé',
    partially_received: 'Reçu partiel',
    received: 'Reçu complet',
    cancelled: 'Annulé',
  }
  return (
    <span className={`text-[11px] px-2 py-1 rounded-full border ${map[s] || map.draft}`}>
      {label[s] || status || '—'}
    </span>
  )
}

export default function PurchaseOrderView() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [po, setPo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [working, setWorking] = useState(false)

  const cardBase = 'bg-surface-card border border-border rounded-xl shadow-sm'

  async function load() {
    setLoading(true)
    try {
      const data = await apiFetch(`/api/purchase-orders/${id}`)
      setPo(data)
    } catch (e) {
      alert('Erreur chargement BC: ' + e.message)
      setPo(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [id]) // eslint-disable-line

  const totalHT = useMemo(() => {
    const lines = po?.lines || []
    return lines.reduce((s, l) => s + Number(l.qty_ordered ?? 0) * Number(l.unit_cost ?? 0), 0)
  }, [po])

  async function validatePO() {
    if (!po) return
    setWorking(true)
    try {
      await apiFetch(`/api/purchase-orders/${po.id}/validate`, { method: 'POST' })
      await load()
      alert('Bon de commande validé ✅')
    } catch (e) {
      alert('Validation impossible: ' + e.message)
    } finally {
      setWorking(false)
    }
  }

  async function downloadPOPDF() {
    try {
      if (!po?.id) return alert('id manquant')
      const blob = await apiFetchBlob(`/api/purchase-orders/${po.id}/pdf`)
      forceDownload(blob, `BC_${po.external_id || po.id}.pdf`)
    } catch (e) {
      alert('PDF BC impossible: ' + e.message)
    }
  }

  async function convertToReceipt() {
    if (!po) return
    const st = (po.status || '').toLowerCase()
    if (st !== 'approved' && st !== 'sent') {
      return alert('Le BC doit être validé (approved) avant conversion.')
    }

    setWorking(true)
    try {
      await apiFetch(`/api/purchase-orders/${po.id}/convert-to-receipt`, {
        method: 'POST',
        body: {},
      })

      alert('BC converti en BA ✅ (stock mis à jour)')
      navigate('/purchases/receive') // si ta page existe, sinon mets /purchases/orders
    } catch (e) {
      alert('Conversion impossible: ' + e.message)
    } finally {
      setWorking(false)
    }
  }

  if (loading) return <div className="p-6 text-content-secondary">Chargement…</div>

  if (!po) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="text-content-primary">BC introuvable.</div>
        <button
          onClick={() => navigate('/purchases/orders')}
          className="mt-3 px-4 py-2 rounded-lg border border-border hover:bg-surface-primary"
        >
          Retour liste
        </button>
      </div>
    )
  }

  const status = (po.status || 'draft').toLowerCase()
  const canValidate = status === 'draft'
  const canConvert = status === 'approved' || status === 'sent'

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-sm text-content-secondary">
            <button className="hover:text-slate-800" onClick={() => navigate('/purchases/orders')}>
              Bons de commande
            </button>{' '}
            / <span className="text-content-primary font-semibold">{po.external_id || po.id}</span>
          </div>

          <h1 className="text-2xl font-extrabold text-content-primary mt-1">Détail Bon de commande</h1>

          <div className="mt-2 flex items-center gap-2">
            <StatusBadge status={po.status} />
            <span className="text-xs text-content-secondary">
              Total HT: <b className="text-content-primary">{money(totalHT)} XOF</b>
            </span>
            {po.tva_pct != null && (
              <span className="text-xs text-content-secondary">
                TVA: <b className="text-content-primary">{Number(po.tva_pct).toLocaleString('fr-FR')}%</b>
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 items-end">
          <button
            onClick={downloadPOPDF}
            className="px-4 py-2 rounded-lg text-sm font-semibold border border-border hover:bg-surface-primary"
          >
            Télécharger PDF
          </button>

          <button
            onClick={validatePO}
            disabled={!canValidate || working}
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition ${
              (!canValidate || working)
                ? 'bg-slate-300 cursor-not-allowed'
                : 'bg-slate-900 hover:bg-slate-950 active:scale-[0.99]'
            }`}
            title={canValidate ? 'Valider le BC' : 'Déjà validé / non disponible'}
          >
            {working && canValidate ? 'Validation…' : 'Valider'}
          </button>

          <button
            onClick={convertToReceipt}
            disabled={!canConvert || working}
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition ${
              (!canConvert || working)
                ? 'bg-slate-300 cursor-not-allowed'
                : 'bg-slate-900 hover:bg-black active:scale-[0.99]'
            }`}
            title={canConvert ? 'Créer un bon d’achat depuis ce BC' : 'Valide d’abord le BC'}
          >
            {working && canConvert ? 'Conversion…' : 'Convertir en Bon d’achat'}
          </button>
        </div>
      </div>

      {/* Supplier */}
      <div className={`${cardBase} p-5 mb-6`}>
        <div className="font-bold text-content-primary mb-3">Fournisseur</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-xs font-medium text-content-secondary">Nom</div>
            <div className="text-sm font-semibold text-content-primary">{po.supplier_name}</div>
          </div>
          <div>
            <div className="text-xs font-medium text-content-secondary">Contact</div>
            <div className="text-sm text-content-primary">{po.supplier_contact || '—'}</div>
          </div>
          <div>
            <div className="text-xs font-medium text-content-secondary">Téléphone</div>
            <div className="text-sm text-content-primary">{po.supplier_phone || '—'}</div>
          </div>
        </div>
      </div>

      {/* Lines */}
      <div className={`${cardBase} overflow-hidden`}>
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="font-bold text-content-primary">Lignes du bon</div>
            <div className="text-xs text-content-secondary">Quantités commandées + coût attendu (HT).</div>
          </div>
          <div className="text-xs text-content-secondary">{(po.lines || []).length} ligne(s)</div>
        </div>

        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary text-content-secondary">
              <tr>
                <th className="p-3 text-left font-semibold">Produit</th>
                <th className="p-3 text-right font-semibold">Qté commandée</th>
                <th className="p-3 text-right font-semibold">PU attendu (HT)</th>
                <th className="p-3 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {(po.lines || []).map((l, idx) => {
                const q = Number(l.qty_ordered ?? 0)
                const u = Number(l.unit_cost ?? 0)
                return (
                  <tr
                    key={l.id || idx}
                    className={`border-t border-border-light ${idx % 2 === 0 ? 'bg-surface-card' : 'bg-surface-primary/40'} hover:bg-slate-50/30 transition`}
                  >
                    <td className="p-3">
                      <div className="font-medium text-content-primary">{l.product_name}</div>
                      <div className="text-xs text-content-secondary">ID: {l.product_id}</div>
                    </td>
                    <td className="p-3 text-right font-semibold">{q}</td>
                    <td className="p-3 text-right">{money(u)} XOF</td>
                    <td className="p-3 text-right font-extrabold">{money(q * u)} XOF</td>
                  </tr>
                )
              })}

              {(po.lines || []).length === 0 && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-content-secondary">Aucune ligne.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-4 border-t border-border flex items-center justify-end">
          <div className="text-right">
            <div className="text-xs text-content-secondary">Total HT</div>
            <div className="text-lg font-extrabold text-content-primary">{money(totalHT)} XOF</div>
          </div>
        </div>
      </div>

      <div className="mt-4 text-xs text-content-secondary">
        Rappel: <b>BC</b> = commande (prévision, stock inchangé). <b>BA</b> = achat/réception (réel, stock augmente).
      </div>
    </div>
  )
}
